import React, { useEffect, useState } from "react";
import { Loader2, Save, Settings as SettingsIcon } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LANGS = [["fr", "Français"], ["nl", "Nederlands"], ["en", "English"]];

export default function SettingsManager() {
  const { authHeaders } = useAuth();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get("/admin/settings", { headers: authHeaders() }).then(({ data }) => { setS(data || {}); setLoading(false); }).catch(() => setLoading(false));
  }, [authHeaders]);

  const toggleLang = (code) => setS((p) => {
    const langs = p.available_langs || [];
    return { ...p, available_langs: langs.includes(code) ? langs.filter((l) => l !== code) : [...langs, code] };
  });

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      await api.put("/admin/settings", {
        app_name: s.app_name, logo_url: s.logo_url, primary_lang: s.primary_lang,
        available_langs: s.available_langs, show_unavailable: s.show_unavailable,
      }, { headers: authHeaders() });
      setMsg("Paramètres enregistrés ✓");
    } catch (e) { setMsg(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); setTimeout(() => setMsg(""), 2500); }
  };

  if (loading || !s) return <div className="flex h-40 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-caneat-orange" /></div>;

  return (
    <div className="max-w-xl space-y-5">
      <div className="flex items-center gap-2"><SettingsIcon className="h-6 w-6 text-caneat-orange" /><h1 className="caneat-title text-3xl text-caneat-ink">Paramètres</h1></div>

      <div className="rounded-2xl bg-white p-5 ring-1 ring-caneat-cream space-y-4">
        <h2 className="caneat-title text-lg text-caneat-ink">Général</h2>
        <div><label className="mb-1 block text-xs font-bold uppercase text-caneat-muted">Nom de l'application</label>
          <input value={s.app_name || ""} onChange={(e) => setS({ ...s, app_name: e.target.value })} data-testid="settings-appname"
            className="w-full rounded-xl border-2 border-caneat-cream px-3 py-2 text-sm font-semibold outline-none focus:border-caneat-orange" /></div>
        <div><label className="mb-1 block text-xs font-bold uppercase text-caneat-muted">Langue principale</label>
          <select value={s.primary_lang || "fr"} onChange={(e) => setS({ ...s, primary_lang: e.target.value })}
            className="w-full rounded-xl border-2 border-caneat-cream px-3 py-2 text-sm font-semibold outline-none focus:border-caneat-orange">
            {LANGS.map(([c, l]) => <option key={c} value={c}>{l}</option>)}</select></div>
        <div><label className="mb-2 block text-xs font-bold uppercase text-caneat-muted">Langues disponibles</label>
          <div className="flex gap-2">
            {LANGS.map(([c, l]) => {
              const on = (s.available_langs || []).includes(c);
              return <button key={c} onClick={() => toggleLang(c)} className={`rounded-full border-2 px-4 py-1.5 text-sm font-bold ${on ? "border-caneat-orange bg-caneat-orange text-white" : "border-caneat-cream text-caneat-muted"}`}>{l}</button>;
            })}
          </div></div>
      </div>

      <div className="rounded-2xl bg-white p-5 ring-1 ring-caneat-cream space-y-3">
        <h2 className="caneat-title text-lg text-caneat-ink">Produits</h2>
        <label className="flex cursor-pointer items-center justify-between" data-testid="settings-show-unavailable">
          <span className="text-sm font-semibold text-caneat-ink">Afficher les produits indisponibles (grisés) côté client</span>
          <input type="checkbox" checked={!!s.show_unavailable} onChange={(e) => setS({ ...s, show_unavailable: e.target.checked })} className="h-5 w-5 accent-caneat-orange" />
        </label>
        <p className="text-xs text-caneat-muted">Désactivé : les produits indisponibles sont complètement masqués.</p>
      </div>

      {msg && <p className="text-sm font-bold text-caneat-orange">{msg}</p>}
      <button onClick={save} disabled={saving} data-testid="settings-save"
        className="flex min-h-[50px] w-full items-center justify-center gap-2 rounded-full bg-caneat-orange text-base font-extrabold uppercase text-white disabled:opacity-60">
        {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Save className="h-5 w-5" /> Enregistrer</>}
      </button>
    </div>
  );
}
